import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import QuickPinchZoom, { make3dTransformValue } from 'react-quick-pinch-zoom';

interface PhotoViewerProps {
  /** Photo to display, full screen. The viewer is closed (nothing rendered) while this is empty. */
  url?: string;
  onClose: () => void;
}

/**
 * Full screen photo viewer shared by the tree popups (private and public maps).
 *
 * Rendered through a portal: both popups live on a detached React root (antd's Modal portals its own
 * content to the body, this overlay did not), so without the portal the overlay never reaches the
 * document and clicking a photo looks like it does nothing. Through the portal, `position: fixed`
 * really does mean the viewport.
 *
 * The photo fills the frame with `object-fit: contain`, so the whole photo is visible, scaled to fit.
 * Pinch / wheel zoom it, the `x` button closes the viewer, and so does a tap on the black bars
 * around the photo. It fades in and out.
 */
// Faded with CSS animations (the `photo-viewer-fade-in` / `-out` keyframes in assets/less/main.less)
// rather than a transition: a just mounted element does not transition reliably, and the fade out has
// to be over before the parent is told to close. The duration is set from here so the two stay in
// sync - it is also how long the fade out waits before closing.
const FADE_DURATION_MS = 100;

export default function PhotoViewer(props: PhotoViewerProps) {
  const { url, onClose } = props;
  const imgRef = useRef<HTMLImageElement>(null);
  // Whether the press behind the current click started on the overlay itself, see the click handler
  // below. Deliberately not a timer: whether that click arrives 10ms or 350ms after the tap depends
  // on the browser, while the press that opened the viewer is never on this element.
  const pressedRef = useRef(false);
  // Set while the overlay fades out; the parent is told to close once it is over.
  const [closing, setClosing] = useState(false);
  const closeTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    // The component stays mounted between two photos: it has to fade in again, not stay faded out.
    setClosing(false);
  }, [url]);

  useEffect(() => () => {
    if (closeTimerRef.current !== undefined) {
      window.clearTimeout(closeTimerRef.current);
    }
  }, []);

  if (!url) {
    return null;
  }

  // Closing fades the overlay out first: `onClose` is what unmounts the viewer, so it is called once
  // the fade is over, and a second close while it plays is ignored.
  const close = () => {
    if (closing) {
      return;
    }
    setClosing(true);
    closeTimerRef.current = window.setTimeout(onClose, FADE_DURATION_MS);
  };

  // The image element is larger than the photo itself: `object-fit: contain` letterboxes the photo
  // inside it. A tap on the black bars must therefore fall through to the overlay (which closes the
  // viewer), while a tap on the photo is swallowed. The photo is centred, so its displayed rectangle
  // is its natural size scaled by the smaller of the two fit ratios.
  const stopClickOnPhoto = (e: React.MouseEvent<HTMLImageElement>) => {
    const element = e.currentTarget;
    const rect = element.getBoundingClientRect();
    const naturalWidth = element.naturalWidth;
    const naturalHeight = element.naturalHeight;
    if (!naturalWidth || !naturalHeight || !rect.width || !rect.height) {
      e.stopPropagation();
      return;
    }
    const fit = Math.min(rect.width / naturalWidth, rect.height / naturalHeight);
    const photoWidth = naturalWidth * fit;
    const photoHeight = naturalHeight * fit;
    const offsetX = e.clientX - rect.left - (rect.width - photoWidth) / 2;
    const offsetY = e.clientY - rect.top - (rect.height - photoHeight) / 2;
    if (offsetX >= 0 && offsetY >= 0 && offsetX <= photoWidth && offsetY <= photoHeight) {
      e.stopPropagation();
    }
  };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'black',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        // `forwards` keeps the viewer at the end of the fade in until the fade out replaces it.
        animationName: closing ? 'photo-viewer-fade-out' : 'photo-viewer-fade-in',
        animationDuration: `${FADE_DURATION_MS}ms`,
        animationTimingFunction: 'ease',
        animationFillMode: 'forwards'
      }}
      // Tapping the black area around the photo closes the viewer, but only when the press started
      // here: the tap that opened the viewer is followed by the click of that same tap, and a phone
      // delivers it to whatever is on top by then - this overlay - so the viewer closed as soon as
      // it appeared. It only showed with a landscape photo: that click lands on the black bars above
      // or below the photo, exactly where the geometric test on the image lets it through.
      onPointerDown={() => { pressedRef.current = true; }}
      onClick={() => {
        const pressedOnOverlay = pressedRef.current;
        pressedRef.current = false;
        if (pressedOnOverlay) {
          close();
        }
      }}
    >
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        display: 'flex',
        gap: '15px',
        zIndex: 10001
      }}>
        <Button
          type="primary"
          shape="circle"
          size="large"
          icon={<CloseOutlined />}
          onClick={close}
          style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: 'none' }}
        />
      </div>
      <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
        <QuickPinchZoom
          // The library's own container only gets `overflow: hidden; touch-action: none` - no size -
          // so `height: 100%` on the image below would fall back to its natural height (a portrait
          // photo then fills the width and overflows the screen). Sized to the viewport, the image
          // fills the frame and `object-fit: contain` shows the whole photo, scaled to fit.
          containerProps={{ style: { width: '100%', height: '100%' } }}
          onUpdate={({ x, y, scale }) => {
            if (imgRef.current) {
              imgRef.current.style.setProperty('transform', make3dTransformValue({ x, y, scale }));
            }
          }}
          enforceBoundsDuringZoom={true}
          draggableUnZoomed={false}
        >
          <img
            ref={imgRef}
            src={url}
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            alt="tree full view"
            // Tapping the photo keeps the viewer open; tapping the black bars around it closes it.
            onClick={stopClickOnPhoto}
          />
        </QuickPinchZoom>
      </div>
    </div>,
    document.body
  );
}
